import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackDealsView, trackDealsClick } from '../services/analytics';
import './CardDeals.css';

const isVideoDeal = (deal) =>
  deal?.mediaType === 'video' && typeof deal?.mediaUrl === 'string' && deal.mediaUrl.length > 0;

const CardDeals = ({ deals }) => {
  const [loadedImages, setLoadedImages] = useState({});
  const [selectedDeal, setSelectedDeal] = useState(null);
  const navigate = useNavigate();
  const cardRefs = useRef({});
  const videoRefs = useRef({});
  const trackedDealsRef = useRef(new Set()); // Éviter les doublons
  const observerRef = useRef(null);
  const videoVisibilityObserverRef = useRef(null);

  const handleImageLoad = useCallback((id) => {
    setLoadedImages(prev => ({
      ...prev,
      [id]: true
    }));
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const day = date.getDate();
      const month = date.getMonth();
      const year = date.getFullYear();
      
      const moisFr = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
      ];
      
      return `${day} ${moisFr[month]} ${year}`;
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return 'Date non disponible';
    }
  };

  const handleCardClick = (deal, actionType = 'direct_redirect') => {
    // Tracker le clic en arrière-plan (non bloquant)
    trackDealsClick(
      deal.id,
      deal.titre || 'Sans titre',
      deal.type || 'unknown',
      actionType,
      deal.urlRedirection
    ).catch(() => {
      // Erreur silencieuse
    });
    
    // Redirection immédiate (ne pas attendre le tracking)
    if (deal.urlRedirection) {
      const fullUrl = deal.urlRedirection.startsWith('http') 
        ? deal.urlRedirection 
        : `https://${deal.urlRedirection}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const redirectToStores = (deal) => {
    // Tracker le clic "Voir en boutique"
    trackDealsClick(
      deal.id,
      deal.titre || 'Sans titre',
      deal.type || 'unknown',
      'voir_en_boutique'
    ).catch(() => {});
    
    navigate('/shops');
  };

  // Tracking des vues avec Intersection Observer
  useEffect(() => {
    if (!deals || deals.length === 0) return;

    // Nettoyer l'observer précédent
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const observerOptions = {
      root: null,                    // Viewport
      rootMargin: '0px',
      threshold: 0.3                  // 30% de visibilité minimum
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const dealId = entry.target.dataset.dealId;
          
          // Vérifier qu'on n'a pas déjà tracké ce deal
          if (dealId && !trackedDealsRef.current.has(dealId)) {
            // Marquer immédiatement comme tracké (synchrone)
            trackedDealsRef.current.add(dealId);
            
            const deal = deals.find(d => d.id === dealId);
            if (deal) {
              // Tracker en arrière-plan (non bloquant)
              trackDealsView(
                deal.id,
                deal.titre || 'Sans titre',
                deal.type || 'unknown'
              ).catch(() => {
                // Erreur silencieuse - déjà gérée par trackError
              });
            }
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observerRef.current = observer;
    
    const observedRefs = new Set();

    const observeCards = () => {
      let observedCount = 0;
      Object.values(cardRefs.current).forEach((ref) => {
        if (ref && !observedRefs.has(ref)) {
          observer.observe(ref);
          observedRefs.add(ref);
          observedCount++;
        }
      });
      
      // Réessayer si aucune carte n'a été observée
      if (observedCount === 0 && deals.length > 0) {
        setTimeout(observeCards, 200);
      }
    };

    // Attendre que le DOM soit prêt
    requestAnimationFrame(() => {
      setTimeout(observeCards, 50);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [deals]);

  // Lecture auto des vidéos uniquement quand la carte est visible (viewport)
  useEffect(() => {
    if (!deals?.length) return;

    const videoRefsMap = videoRefs.current;

    if (videoVisibilityObserverRef.current) {
      videoVisibilityObserverRef.current.disconnect();
      videoVisibilityObserverRef.current = null;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.45
    };

    const onVideoVisibility = (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (!(video instanceof HTMLVideoElement)) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    };

    const observer = new IntersectionObserver(onVideoVisibility, observerOptions);
    videoVisibilityObserverRef.current = observer;

    const videoDealCount = deals.filter(isVideoDeal).length;

    const observeVideos = () => {
      let observed = 0;
      deals.forEach((d) => {
        if (!isVideoDeal(d)) return;
        const el = videoRefs.current[d.id];
        if (el) {
          observer.observe(el);
          observed += 1;
        }
      });
      if (videoDealCount > 0 && observed === 0) {
        setTimeout(observeVideos, 200);
      }
    };

    requestAnimationFrame(() => {
      setTimeout(observeVideos, 50);
    });

    return () => {
      Object.values(videoRefsMap).forEach((v) => {
        if (v instanceof HTMLVideoElement) {
          v.pause();
        }
      });
      if (videoVisibilityObserverRef.current) {
        videoVisibilityObserverRef.current.disconnect();
        videoVisibilityObserverRef.current = null;
      }
    };
  }, [deals]);

  const handleShowDetails = (deal) => {
    // Tracker le clic "En savoir plus"
    trackDealsClick(
      deal.id,
      deal.titre || 'Sans titre',
      deal.type || 'unknown',
      'en_savoir_plus',
      deal.urlRedirection
    ).catch(() => {});

    switch (deal.type) {
      case 'store':
        if (deal.dateDebut || deal.dateFin || deal.conditions) {
          setSelectedDeal(deal);
        } else {
          redirectToStores(deal);
        }
        break;

      case 'both':
        setSelectedDeal(deal);
        break;

      case 'online':
        if (!deal.dateDebut && !deal.dateFin && !deal.conditions) {
          handleCardClick(deal, 'direct_redirect');
        } else {
          setSelectedDeal(deal);
        }
        break;

      case 'announcement':
        if (deal.urlRedirection) {
          handleCardClick(deal, 'direct_redirect');
        }
        break;

      default:
        console.warn('Type de deal non reconnu:', deal.type);
    }
  };

  const handleCloseModal = () => {
    setSelectedDeal(null);
  };

  return (
    <>
      <div className="deals-cards-container">
        {deals.length === 0 ? (
          <div className="deals-empty-state">
            <video 
              ref={(video) => {
                if (video) {
                  video.addEventListener('loadeddata', () => {
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                      playPromise.catch(error => {
                        console.log('Autoplay prevented, user interaction needed');
                      });
                    }
                  });
                }
              }}
              loop 
              muted 
              playsInline
              preload="auto"
              controls={false}
              disablePictureInPicture
              webkit-playsinline="true"
              onTouchStart={(e) => {
                e.currentTarget.play();
              }}
              onClick={(e) => {
                e.currentTarget.play();
              }}
              style={{
                width: '300px',
                height: '300px',
                borderRadius: '20px',
                objectFit: 'cover',
                marginBottom: '1rem',
                cursor: 'pointer'
              }}
            >
              <source src="/panda.mp4" type="video/mp4" />
              <source src="/panda.webm" type="video/webm" />
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
            <p>Bons plans à venir prochainement ...</p>
          </div>
        ) : (
          deals.map((item, index) => (
            <article 
              key={item.id} 
              ref={(el) => { cardRefs.current[item.id] = el; }}
              data-deal-id={item.id}
              className="card-deals"
            >
              <div className="card-deals__image-container">
                {isVideoDeal(item) ? (
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current[item.id] = el;
                      else delete videoRefs.current[item.id];
                    }}
                    className={`card-deals__image card-deals__media-video ${loadedImages[item.id] ? 'loaded' : ''}`}
                    src={item.mediaUrl}
                    poster={item.imageUrl || undefined}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    controls={false}
                    disablePictureInPicture
                    aria-label={item.titre || 'Vidéo bon plan'}
                    onLoadedData={() => handleImageLoad(item.id)}
                    onCanPlay={() => handleImageLoad(item.id)}
                  />
                ) : (
                  <img
                    src={item.imageUrl}
                    alt={item.titre}
                    className={`card-deals__image ${loadedImages[item.id] ? 'loaded' : ''}`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority={index < 2 ? 'high' : 'low'}
                    onLoad={() => handleImageLoad(item.id)}
                    width="100%"
                    height="auto"
                  />
                )}
                {!loadedImages[item.id] && (
                  <div className="card-deals__image-placeholder">
                  </div>
                )}
              </div>
              
              <div className="card-deals__content">
                {!(item.type === 'announcement' && !item.urlRedirection) && (
                  <button 
                    className="card-deals__button"
                    onClick={() => handleShowDetails(item)}
                  >
                    En savoir plus
                  </button>
                )}
              </div>
            </article>
          ))
        )}
      </div>

      {selectedDeal && (
        <div 
          className={`card-deals__modal-overlay ${selectedDeal ? 'show' : ''}`}
          onClick={handleCloseModal}
        >
          <div 
            className="card-deals__modal"
            onClick={e => e.stopPropagation()}
          >
            <button 
              className="card-deals__modal-close"
              onClick={handleCloseModal}
              aria-label="Fermer"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M6.225 4.811a1 1 0 00-1.414 1.414L10.586 12 4.81 17.775a1 1 0 101.414 1.414L12 13.414l5.775 5.775a1 1 0 001.414-1.414L13.414 12l5.775-5.775a1 1 0 00-1.414-1.414L12 10.586 6.225 4.81z"/>
              </svg>
            </button>
            <div className={`card-deals__modal-content ${
              selectedDeal.type === 'both' && !selectedDeal.conditions && !selectedDeal.dateDebut ? 
              'both-choices' : ''
            }`}>
              {selectedDeal.type === 'both' && !selectedDeal.conditions && !selectedDeal.dateDebut ? (
                <div className="card-deals__modal-content">
                  <div className="card-deals__info-message">
                    <h3>Cette offre est disponible :</h3>
                    <p>En ligne sur le site web ou directement en magasin près de chez vous.</p>
                  </div>
                  <div className="card-deals__actions dual-buttons">
                    <button 
                      className="card-deals__redirect online"
                      onClick={() => handleCardClick(selectedDeal, 'voir_en_ligne')}
                    >
                      Voir en ligne
                    </button>
                    <button 
                      className="card-deals__redirect store"
                      onClick={() => redirectToStores(selectedDeal)}
                    >
                      Voir en boutique
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {selectedDeal.dateDebut && selectedDeal.dateFin && (
                    <div className="card-deals__validity">
                      <span>Du {formatDate(selectedDeal.dateDebut)} au {formatDate(selectedDeal.dateFin)}</span>
                    </div>
                  )}
                  {selectedDeal.conditions && (
                    <div className="card-deals__conditions">
                      <h3>Conditions</h3>
                      <p>{selectedDeal.conditions}</p>
                    </div>
                  )}
                  <div className={`card-deals__actions ${selectedDeal.type === 'both' ? 'dual-buttons' : ''}`}>
                    {selectedDeal.type === 'both' ? (
                      <>
                        <button 
                          className="card-deals__redirect online"
                          onClick={() => handleCardClick(selectedDeal, 'voir_en_ligne')}
                        >
                          Voir en ligne
                        </button>
                        <button 
                          className="card-deals__redirect store"
                          onClick={() => redirectToStores(selectedDeal)}
                        >
                          Voir en boutique
                        </button>
                      </>
                    ) : selectedDeal.type === 'store' ? (
                      <button 
                        className="card-deals__redirect store"
                        onClick={() => redirectToStores(selectedDeal)}
                      >
                        Voir en boutique
                      </button>
                    ) : (
                      <button 
                        className="card-deals__redirect online"
                        onClick={() => handleCardClick(selectedDeal, 'voir_en_ligne')}
                      >
                        Voir en ligne
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardDeals; 